import { ReactNode } from "react"

import { v7 as uuidv7 } from "uuid"
import { createStore, StoreApi, useStore } from "zustand"

import { ValueOf } from "../lib/any"

export const FlashKind = {
  Info: "info",
  Success: "success",
  Error: "error",
  Warning: "warning",
  Danger: "danger",
} as const

export type FlashKind = ValueOf<typeof FlashKind>

export const FlashLifespan = {
  Now: "now",
  Redirect: "redirect",
  Indefinite: "indefinite",
} as const

export type FlashLifespan = ValueOf<typeof FlashLifespan>

export type FlashOptions = {
  /**
   * How many seconds the flash with last before being deleted.
   */
  duration?: number
  /**
   * In a page navigation, how many requests should the flash last. `now` is intended for the current page,
   * `redirect` is intended to last to next the page after a redirect, and `indefinite` is intended to last as
   * long as the flash is in memory.
   */
  lifespan?: FlashLifespan
}

/**
 * A flash message that can be displayed to the user.
 *
 * @property id - Unique identifier for the flash message
 * @property children - The content of the message, can be any React node (text, JSX, etc.)
 * @property kind - The type/severity of the message ("info" | "success" | "error" | "warning" | "danger")
 * @property duration - Auto-dismiss duration in milliseconds. If not provided, message persists until manually dismissed or aged out.
 * @property age - The number of page navigations the message should persist for. It is decremented on each navigation. When it reaches 0, the message is removed.
 */
export type FlashMessage = {
  id: string
  children: ReactNode
  kind: FlashKind
  age?: number
} & FlashOptions

/**
 * A record mapping flash message kinds to React nodes.
 * Can be a partial record.
 * Used for bulk operations when adding multiple flash messages at once.
 *
 * @example
 * ```ts
 * const messages: FlashRecord = {
 *   success: "Operation completed successfully",
 * }
 * ```
 */
export type FlashRecord = Partial<Record<FlashKind, React.ReactNode>>

/**
 * Parameters to create a new flash message.
 */
export type FlashMessageNew = Omit<FlashMessage, "id">

/**
 * The store that manages the state of flash messages.
 * Provides methods to add, remove, and manage flash notifications throughout the application.
 */
export type FlasherStore = {
  /** Array of currently active flash messages */
  messages: FlashMessage[]

  /**
   * Adds multiple flash messages to the store from FlashMessageNew or FlashRecord objects.
   * @param messages - Array of `FlashMessageNew` or `FlashRecord` objects to add.
   * @param options - Optional `FlashOptions` to apply to all messages being pushed. Options on individual messages take precedence.
   */
  push(messages?: Array<FlashMessageNew | FlashRecord>, options?: FlashOptions): void

  /**
   * Adds a single flash message with the specified kind and content with an `age` of 2 expected to be shown after
   * a redirect, then disappear after a subsequent navigation.
   * @param kind - The type/severity of the message
   * @param children - The message content (can be text or JSX)
   * @param options - Optional configuration (e.g., auto-dismiss duration)
   */
  flash: (kind: FlashKind, children: ReactNode, options?: FlashOptions) => void

  /**
   * Adds a single flash message with the specified kind and content with an `age` of 1 expected to be shown on the current
   * page, then disappear after a navigation.
   * @param kind - The type/severity of the message
   * @param children - The message content (can be text or JSX)
   * @param options - Optional configuration (e.g., auto-dismiss duration)
   */
  flashNow: (kind: FlashKind, children: ReactNode, options?: FlashOptions) => void

  /**
   * Decrements the `age` of all messages by 1. If a message's `age` reaches
   * 0, it is deleted. Messages without an `age` property are ignored.
   */
  ageFlashes: () => void

  /**
   * Removes a specific flash message by its ID.
   * @param id - The unique ID of the message to remove
   */
  remove: (id: string) => void

  /**
   * Removes all flash messages from the store.
   */
  clearAll: () => void
}

const defaultOptions: FlashOptions = {
  duration: Infinity,
  lifespan: FlashLifespan.Redirect,
}

export const flasherStore = createStore<FlasherStore>()((set, get) => ({
  messages: [],
  push(messages?: Array<FlashMessageNew | FlashRecord>, options?: FlashOptions) {
    if (messages == null) return

    const newMessages: Array<FlashMessage> = messages.flatMap((message) => {
      // It's a FlashMessageNew if it has a `kind` and `children` property.
      if ("kind" in message && "children" in message) {
        const msg = message as FlashMessageNew

        const { duration, lifespan } = { ...defaultOptions, ...options, ...msg }
        const age = lifespan === FlashLifespan.Now ? 1 : lifespan === FlashLifespan.Redirect ? 2 : Infinity

        return {
          id: uuidv7(),
          kind: msg.kind,
          children: msg.children,
          age,
          duration,
        }
      }

      // It's a FlashRecord
      const record = message as FlashRecord
      const { duration, lifespan: life } = { ...defaultOptions, ...options }
      const age = life === FlashLifespan.Now ? 1 : life === FlashLifespan.Redirect ? 2 : Infinity

      return Object.entries(record).map(([kind, children]) => ({
        id: uuidv7(),
        kind: kind as FlashKind,
        children,
        age,
        duration,
      }))
    })

    set((state) => ({ messages: [...state.messages, ...newMessages] }))

    // Queue deletions of flashes with a duration
    newMessages.forEach((message) => {
      const { duration } = message

      if (duration != null && isFinite(duration) && duration > 0) {
        setTimeout(() => {
          get().remove(message.id)
        }, duration)
      }
    })
  },
  flash: (kind, children, options) => {
    get().push([{ kind, children }], { ...options })
  },
  flashNow: (kind, children, options) => {
    get().push([{ kind, children }], {
      lifespan: FlashLifespan.Now,
      ...options,
    })
  },
  ageFlashes: () => {
    set((state) => ({
      messages: state.messages
        .map((message) => {
          if (typeof message.age === "number" && message.age !== Infinity) {
            return {
              ...message,
              age: message.age - 1,
            }
          }
          return message
        })
        .filter((msg) => typeof msg.age !== "number" || msg.age === Infinity || msg.age > 0),
    }))
  },
  remove: (id) => {
    set((state) => ({
      messages: state.messages.filter((msg) => msg.id !== id),
    }))
  },
  clearAll: () => set({ messages: [] }),
}))

declare global {
  interface Window {
    flasherStore: StoreApi<FlasherStore>
  }
}

// Make it available on the window for interop, following useRequestCart example.
window.flasherStore = flasherStore

/**
 * Custom hook to access the flasher store.
 *
 * This hook utilizes the `useStore` hook to retrieve the state and actions
 * from the `flasherStore`. It provides an easy way to interact with the
 * flasher within the application.
 *
 * @returns The state and actions from the flasher store.
 */
export function useFlasher() {
  const store = useStore(flasherStore)

  return store
}
